import type { SerializedConstructor } from '@langchain/core/load/serializable';
import { TemplateFormat } from '@langchain/core/prompts';

import {
  EvaluatedEntityType,
  SchemaDefinition,
  SchemaProperty,
} from '@/components/EvaluatorCrudPane/utils/serializeFeedbackUIToLC';

import { StructuredPromptSchema } from '../Playground/components/manifest/serialized/prompts/ManifestStructuredPrompt';

const defaultSessionRulePromptMessages = (templateFormat) => {
  const value = 'constructor' as const;
  return [
    {
      id: ['langchain', 'prompts', 'chat', 'SystemMessagePromptTemplate'],
      lc: 1,
      type: value,
      kwargs: {
        prompt: {
          id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
          lc: 1,
          type: value,
          kwargs: {
            template: `First, describe the persona of your evaluator (e.g. "You are an expert data labeler evaluating model outputs for conciseness").

<Rubric>
Provide bullet points for how the LLM should score both correct and incorrect examples, e.g.:
A perfectly concise answer:
- Contains only the exact information requested.
- Uses the minimum number of words necessary to convey the complete answer.
When scoring, you should deduct points for:
- Redundant information or restatements.
- Polite phrases like "hope this helps" or "let me know if you need anything else."
</Rubric>

<Instructions>
Provide instructions for how the LLM should process the example, e.g.:
- Carefully read the input and output.
- Check for any unnecessary elements, particularly those mentioned in the <Rubric> above.
- The score should reflect how close the response comes to containing only the essential information requested based on the rubric above.
</Instructions>

<Reminder>
Provide any remaining important reminders for the LLM to keep in mind while grading, e.g.:
- The goal is to reward responses that provide complete answers with absolutely no extraneous information.
</Reminder>`,
            input_variables: ['input', 'output'],
            template_format: templateFormat ?? 'f-string',
          },
        },
      },
    },
    {
      id: ['langchain', 'prompts', 'chat', 'HumanMessagePromptTemplate'],
      lc: 1,
      type: value,
      kwargs: {
        prompt: {
          id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
          lc: 1,
          type: value,
          kwargs: {
            template: `Please grade the following example according to the above instructions:

<example>
<input>
${templateFormat === 'mustache' ? '{{input}}' : '{input}'}
</input>

<output>
${templateFormat === 'mustache' ? '{{output}}' : '{output}'}
</output>
</example>`,
            input_variables: ['input', 'output'],
            template_format: templateFormat ?? 'mustache',
          },
        },
      },
    },
  ];
};

const defaultThreadRulePromptMessages = (templateFormat) => {
  const value = 'constructor' as const;
  return [
    {
      id: ['langchain', 'prompts', 'chat', 'SystemMessagePromptTemplate'],
      lc: 1,
      type: value,
      kwargs: {
        prompt: {
          id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
          lc: 1,
          type: value,
          kwargs: {
            template: `You are an expert conversation evaluator. You will be shown a full conversation between a human user and an AI assistant.

Your task is to judge overall user satisfaction with the assistant’s responses.

Provide a single binary score:
- 1 if the user is likely satisfied with the interaction as a whole.
- 0 if the user is likely dissatisfied.
Only output the number 1 or 0 with no explanation.
`,
            input_variables: ['conversation'],
            template_format: templateFormat ?? 'mustache',
          },
        },
      },
    },
    {
      id: ['langchain', 'prompts', 'chat', 'HumanMessagePromptTemplate'],
      lc: 1,
      type: value,
      kwargs: {
        prompt: {
          id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
          lc: 1,
          type: value,
          kwargs: {
            template: `Please grade the following conversation according to the above instructions:

<conversation>
${templateFormat === 'mustache' ? '{{all_messages}}' : '{all_messages}'}
</conversation>
`,
            input_variables: ['all_messages'],
            template_format: templateFormat ?? 'mustache',
          },
        },
      },
    },
  ];
};

const defaultEvaluatorPromptMessages = (templateFormat) => {
  const value = 'constructor' as const;
  return [
    {
      id: ['langchain', 'prompts', 'chat', 'SystemMessagePromptTemplate'],
      lc: 1,
      type: value,
      kwargs: {
        prompt: {
          id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
          lc: 1,
          type: value,
          kwargs: {
            template: `First, describe the persona of your evaluator (e.g. "You are an expert at comparing two answers")

<Rubric>
Provide bullet points for how the LLM should score both correct and incorrect examples, e.g.:
A correct answer:
- Matches the reference answer exactly
- etc.

An incorrect answer:
- Differs from the reference answer
- etc.
</Rubric>

<Instructions>
Provide instructions for how the LLM should process the example, e.g.:
- Find all facts in the reference answer and provided answer
- etc.
</Instructions>

<Reminder>
Provide any remaining important reminders for the LLM to keep in mind while grading, e.g.:
- Different words with the same semantic meaning are the same
</Reminder>`,
            input_variables: ['input', 'output', 'reference'],
            template_format: templateFormat ?? 'mustache',
          },
        },
      },
    },
    {
      id: ['langchain', 'prompts', 'chat', 'HumanMessagePromptTemplate'],
      lc: 1,
      type: value,
      kwargs: {
        prompt: {
          id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
          lc: 1,
          type: value,
          kwargs: {
            template: `Please grade the following example according to the above instructions:

<example>
<input>
${templateFormat === 'mustache' ? '{{input}}' : '{input}'}
</input>

<output>
${templateFormat === 'mustache' ? '{{output}}' : '{output}'}
</output>

<reference_outputs>
${templateFormat === 'mustache' ? '{{reference}}' : '{reference}'}
</reference_outputs>
</example>`,
            input_variables: ['input', 'output', 'reference'],
            template_format: templateFormat ?? 'mustache',
          },
        },
      },
    },
  ];
};

export function emptyStructuredPromptManifest(
  variant: EvaluatedEntityType | undefined,
  inputVar?: string | null,
  templateFormat?: TemplateFormat
): SerializedConstructor {
  const kwargs: {
    messages: SerializedConstructor[];
    input_variables: string[];
  } = {
    messages: [
      {
        id: ['langchain', 'prompts', 'chat', 'SystemMessagePromptTemplate'],
        lc: 1,
        type: 'constructor',
        kwargs: {
          prompt: {
            id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
            lc: 1,
            type: 'constructor',
            kwargs: {
              template: 'You are a chatbot.',
              input_variables: [],
              template_format: templateFormat ?? 'f-string',
            },
          },
        },
      },
      {
        id: ['langchain', 'prompts', 'chat', 'HumanMessagePromptTemplate'],
        lc: 1,
        type: 'constructor',
        kwargs: {
          prompt: {
            id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
            lc: 1,
            type: 'constructor',
            kwargs: {
              template:
                templateFormat === 'mustache'
                  ? `{{${inputVar ?? 'question'}}}`
                  : `{${inputVar ?? 'question'}}`,
              input_variables: [`${inputVar ? inputVar : 'question'}`],
              template_format: templateFormat ?? 'f-string',
            },
          },
        },
      },
    ],
    input_variables: [`${inputVar ? inputVar : 'question'}`],
  };

  if (variant === 'session-eval') {
    kwargs.input_variables = ['input', 'output'];
    kwargs.messages = defaultSessionRulePromptMessages(templateFormat);
  }

  if (variant === 'dataset-eval') {
    kwargs.input_variables = ['input', 'output', 'reference_output'];
    kwargs.messages = defaultEvaluatorPromptMessages(templateFormat);
  }

  if (variant === 'thread-eval') {
    kwargs.input_variables = ['conversation'];
    kwargs.messages = defaultThreadRulePromptMessages(templateFormat);
  }

  return {
    lc: 1,
    type: 'constructor',
    id: ['langchain_core', 'prompts', 'structured', 'StructuredPrompt'],
    kwargs: {
      ...kwargs,
      ...emptyOutputSchema(),
    },
  };
}
export function emptyChatPromptManifest(
  inputVar?: string | null,
  templateFormat?: TemplateFormat
): SerializedConstructor {
  return {
    lc: 1,
    type: 'constructor',
    id: ['langchain', 'prompts', 'chat', 'ChatPromptTemplate'],
    kwargs: {
      messages: [
        {
          id: ['langchain', 'prompts', 'chat', 'SystemMessagePromptTemplate'],
          lc: 1,
          type: 'constructor',
          kwargs: {
            prompt: {
              id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
              lc: 1,
              type: 'constructor',
              kwargs: {
                template: 'You are a chatbot.',
                input_variables: [],
                template_format: templateFormat ?? 'f-string',
              },
            },
          },
        },
        {
          id: ['langchain', 'prompts', 'chat', 'HumanMessagePromptTemplate'],
          lc: 1,
          type: 'constructor',
          kwargs: {
            prompt: {
              id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
              lc: 1,
              type: 'constructor',
              kwargs: {
                template:
                  templateFormat === 'mustache'
                    ? `{{${inputVar ?? 'question'}}}`
                    : `{${inputVar ?? 'question'}}`,
                input_variables: [`${inputVar ? inputVar : 'question'}`],
                template_format: templateFormat ?? 'f-string',
              },
            },
          },
        },
      ],
      input_variables: [`${inputVar ? inputVar : 'question'}`],
    },
  };
}
export function defaultEvaluatorPromptManifest(
  evaluatorName: string
): SerializedConstructor {
  return {
    lc: 1,
    type: 'constructor',
    id: ['langchain_core', 'prompts', 'structured', 'StructuredPrompt'],
    kwargs: {
      messages: [
        {
          lc: 1,
          type: 'constructor',
          id: [
            'langchain_core',
            'prompts',
            'chat',
            'SystemMessagePromptTemplate',
          ],
          kwargs: {
            prompt: {
              lc: 1,
              type: 'constructor',
              id: ['langchain_core', 'prompts', 'prompt', 'PromptTemplate'],
              kwargs: {
                input_variables: [],
                template_format: 'mustache',
                template: 'Write your evaluator prompt here.',
              },
            },
          },
        },
        {
          lc: 1,
          type: 'constructor',
          id: [
            'langchain_core',
            'prompts',
            'chat',
            'HumanMessagePromptTemplate',
          ],
          kwargs: {
            prompt: {
              lc: 1,
              type: 'constructor',
              id: ['langchain_core', 'prompts', 'prompt', 'PromptTemplate'],
              kwargs: {
                input_variables: ['inputs', 'outputs', 'reference_outputs'],
                template_format: 'mustache',
                template:
                  '<input>\n{{input}}\n</input>\n\n<output>\n{{output}}\n</output>\n\n<referenceOutput>\n{{referenceOutput}}\n</referenceOutput>',
              },
            },
          },
        },
      ],
      template_format: 'mustache',
      input_variables: ['question'],
      schema_: {
        title: 'extract',
        description: "Extract information from the user's response.",
        type: 'object',
        properties: {
          [evaluatorName]: {
            type: 'integer',
            enum: [0, 1],
          },
        },
        required: [evaluatorName],
        strict: true,
        additionalProperties: false,
      },
    },
  };
}
export function emptyPromptManifest(
  templateFormat?: TemplateFormat
): SerializedConstructor {
  return {
    lc: 1,
    type: 'constructor',
    id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
    kwargs: {
      template: templateFormat === 'mustache' ? '{{question}}' : '{question}',
      input_variables: ['question'],
      template_format: templateFormat ?? 'f-string',
    },
  };
}

export function emptyOutputSchema(): Record<string, StructuredPromptSchema> {
  return {
    schema_: {
      title: 'extract',
      description: "Extract information from the user's response.",
      type: 'object',
      properties: {
        correctness: {
          type: 'boolean',
          description: 'Is the submission correct, accurate, and factual?',
        },
      },
      required: ['correctness'],
    },
  };
}

export function defaultEvaluatorOutputSchema(
  type: 'dataset-eval' | 'session-eval' | 'thread-eval'
): SchemaDefinition {
  const properties: Record<string, SchemaProperty> = {};

  if (type === 'session-eval') {
    properties.conciseness = {
      type: 'boolean',
      description: 'Is the output concise?',
    };
  } else if (type === 'thread-eval') {
    properties.satisfaction = {
      type: 'boolean',
      description: 'Is the user satisfied with the conversation?',
    };
  } else {
    properties.correctness = {
      type: 'boolean',
      description: 'Is the submission correct, accurate, and factual?',
    };
  }

  const propertyKey = Object.keys(properties)[0];

  return {
    title: 'extract',
    description: "Extract information from the user's response.",
    type: 'object',
    properties,
    required: [propertyKey],
  };
}

export const emptyO1ChatPromptManifest = (
  inputVar?: string | null,
  templateFormat?: TemplateFormat
): SerializedConstructor => {
  return {
    lc: 1,
    type: 'constructor',
    id: ['langchain', 'prompts', 'chat', 'ChatPromptTemplate'],
    kwargs: {
      messages: [
        {
          id: ['langchain', 'prompts', 'chat', 'HumanMessagePromptTemplate'],
          lc: 1,
          type: 'constructor',
          kwargs: {
            prompt: {
              id: ['langchain', 'prompts', 'prompt', 'PromptTemplate'],
              lc: 1,
              type: 'constructor',
              kwargs: {
                template:
                  templateFormat === 'mustache'
                    ? `{{${inputVar ?? 'question'}}}`
                    : `{${inputVar ?? 'question'}}`,
                input_variables: [`${inputVar ? inputVar : 'question'}`],
                template_format: templateFormat ?? 'f-string',
              },
            },
          },
        },
      ],
      input_variables: [`${inputVar ? inputVar : 'question'}`],
    },
  };
};
